import { useCallback } from 'react';
import { useApolloClient } from '@apollo/client/react';
import type { AresLookupData } from '@insio/ui-toolkit';
import type { CompanyRegistryCountry } from '@insio/validation';
import { LookupCompanyByRegistryDocument } from '@/graphql';

/**
 * A lookup result plus whether the register carries tax identifiers at all.
 * Assignable to `AresLookupData`, which SupplierModal's `onAresLookup` expects.
 */
export interface CompanyRegistryLookupData extends AresLookupData {
  /** False for SK's RPO: a missing DIČ there means "unknown", not "the subject has none". */
  publishesTaxIds: boolean;
  /** Entry in a public register as printed on documents; null when the register names none. */
  registryEntry: string | null;
}

/**
 * Company lookup in a national business register, for SupplierModal's `onAresLookup`
 * prop and the organization forms.
 * Resolves `null` when the country has no register or the company is not found;
 * rejects on service error.
 */
export function useCompanyRegistryLookup(
  countryCode: CompanyRegistryCountry | null,
  { includeRegistryEntry = false }: { includeRegistryEntry?: boolean } = {}
): (companyId: string) => Promise<CompanyRegistryLookupData | null> {
  const client = useApolloClient();

  return useCallback(
    async (companyId: string) => {
      if (!countryCode) return null;

      // `client.query`, not useLazyQuery — see use-party-validation-gate.ts for rationale.
      const { data } = await client.query({
        query: LookupCompanyByRegistryDocument,
        variables: { countryCode, companyId, includeRegistryEntry },
        fetchPolicy: 'network-only'
      });
      const result = data?.lookupCompanyByRegistry;
      if (!result) return null;
      return {
        companyId: result.companyId,
        name: result.name,
        taxId: result.taxId,
        vatId: result.vatId,
        publishesTaxIds: result.publishesTaxIds,
        registryEntry: result.registryEntry,
        address: result.address
          ? {
              streetName: result.address.streetName,
              buildingNumber: result.address.buildingNumber,
              city: result.address.city,
              postalCode: result.address.postalCode,
              countryCode: result.address.countryCode
            }
          : null
      };
    },
    [client, countryCode, includeRegistryEntry]
  );
}
